// Шторка задачи (/tasks/:id): шапка с управлением, вопросы к человеку, вкладки обзора, переписки, запусков, заявок и истории.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type UIEvent } from 'react'
import { api, uploadFile } from '../api.ts'
import { ActorLabel, AgentLabel, CreatedBy } from '../components/AgentAvatar.tsx'
import { AgentSelect } from '../components/AgentSelect.tsx'
import { DirectionChip, PriorityIcon, ServiceChip, StatusBadge, TaskKindBadge } from '../components/chips.tsx'
import { EscalationCard } from '../components/EscalationCard.tsx'
import { AttachmentGrid, DropOverlay, FilePicker, MAX_FILE_BYTES, useFileDrop } from '../components/Attachments.tsx'
import { Icon } from '../components/Icon.tsx'
import { JobList } from '../components/JobList.tsx'
import { Drawer } from '../components/Layer.tsx'
import { Composer, MessageItem, MessageThread } from '../components/MessageThread.tsx'
import { SubtaskProgress, waitingText } from '../components/TaskCardView.tsx'
import { Badge, Button, Collapsible, Empty, ErrorBox, IconButton, LiveDot, Loading, Tabs, cx } from '../components/ui.tsx'
import { useAsync, useBusy, useDebounced } from '../lib/hooks.ts'
import {
  INTENT_KIND_LABELS,
  INTENT_KIND_TONES,
  PRIORITY_LABELS,
  RISK_LABELS,
  formatDateTime,
  timeAgo,
  timeUntil,
} from '../lib/format.ts'
import { Markdown } from '../lib/markdown.tsx'
import { agentTime } from '../types.ts'
import { isDoneUnseen, outcomeText } from '../lib/outcome.ts'
import { Link, TaskLink, closeTask, navigate } from '../router.tsx'
import { refreshStats, useAgentMap, useLive, useNow, useReconnect, useStream } from '../store.ts'
import { toast, toastError } from '../toast.ts'
import {
  SAFETY_MODE_LABELS,
  TASK_STATUSES,
  TASK_STATUS_LABELS,
  type Activity,
  type Intent,
  type Message,
  type Priority,
  type SafetyMode,
  type Task,
  type TaskCard,
  type AttachmentInfo,
  type TaskDetail,
  type TaskStatus,
  type TaskNow,
} from '../types.ts'

type TabId = 'overview' | 'thread' | 'jobs' | 'intents' | 'history'

function upsertById<T extends { id: number }>(list: T[], item: T, prepend = false): T[] {
  const i = list.findIndex((x) => x.id === item.id)
  if (i === -1) return prepend ? [item, ...list] : [...list, item]
  const next = [...list]
  next[i] = { ...next[i]!, ...item }
  return next
}

export function TaskDrawer({ id }: { id: number }) {
  return (
    <Drawer onClose={closeTask} label={`Задача OMA-${id}`}>
      <TaskView key={id} id={id} />
    </Drawer>
  )
}

function TaskView({ id }: { id: number }) {
  const detail = useAsync((signal) => api.get<TaskDetail>(`/api/tasks/${id}`, { signal }), [id])
  const { data, setData } = detail
  const refetchSoon = useDebounced(detail.reload, 600)
  const [tab, setTab] = useState<TabId>('overview')
  const outerRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const stickBottom = useRef(true)
  // на компьютере прокручивается тело шторки, на телефоне (тело overflow-visible) — шторка целиком
  const scroller = () => {
    const body = bodyRef.current
    if (body && getComputedStyle(body).overflowY !== 'visible') return body
    return outerRef.current
  }
  // на телефоне шапка уезжает вверх вместе с крестиком — тогда крестик показываем в прилипшей полосе вкладок
  const [headHidden, setHeadHidden] = useState(false)
  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    stickBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60
    if (el === outerRef.current) setHeadHidden(el.scrollTop > 40)
  }
  const pickTab = (t: TabId) => {
    stickBottom.current = true
    // на телефоне новая вкладка открывается с начала: шапка остаётся свёрнутой, вкладки — сверху
    const outer = outerRef.current
    const head = outer?.firstElementChild as HTMLElement | null | undefined
    if (outer && head && scroller() === outer && outer.scrollTop > head.offsetHeight) outer.scrollTop = head.offsetHeight
    setTab(t)
  }

  // вложения: перетащить в окно задачи, вставить скриншот или выбрать кнопкой
  const [uploading, setUploading] = useState<{ name: string; p: number; left: number } | null>(null)
  const addFiles = async (fs: File[]) => {
    const big = fs.filter((f) => f.size > MAX_FILE_BYTES)
    if (big.length) toast(`Больше 100 МБ, не прикреплено: ${big.map((f) => f.name).join(', ')}`, 'error')
    const ok = fs.filter((f) => f.size <= MAX_FILE_BYTES && f.size > 0)
    if (!ok.length || uploading) return
    try {
      const ids: number[] = []
      for (let i = 0; i < ok.length; i++) {
        const f = ok[i]!
        setUploading({ name: f.name, p: 0, left: ok.length - i })
        const a = await uploadFile<AttachmentInfo>(f, (p) => setUploading({ name: f.name, p, left: ok.length - i }))
        ids.push(a.id)
      }
      const r = await api.post<{ attachments: AttachmentInfo[] }>(`/api/tasks/${id}/attachments`, { ids })
      setData((cur) => (cur ? { ...cur, attachments: r.attachments } : cur))
      toast(`Прикреплено файлов: ${ids.length}`, 'success')
    } catch (e) {
      toastError(e, 'Не удалось прикрепить файлы')
    } finally {
      setUploading(null)
    }
  }
  const dragging = useFileDrop((fs) => void addFiles(fs))
  const removeFile = async (a: AttachmentInfo) => {
    try {
      await api.del(`/api/attachments/${a.id}`)
      setData((cur) => (cur ? { ...cur, attachments: (cur.attachments ?? []).filter((x) => x.id !== a.id) } : cur))
    } catch (e) {
      toastError(e, 'Не удалось удалить файл')
    }
  }

  // «Сейчас» считается по всему дереву задачи: движение в нём (запуски, вопросы, статусы) — пересчитать
  const tree = new Set([id, ...(data?.subtasks ?? []).map((s) => s.id), ...(data?.now?.blockers ?? []).map((b) => b.task_id)])
  useEffect(() => {
    const t = window.setInterval(() => {
      if (!document.hidden) detail.reload()
    }, 60_000)
    return () => window.clearInterval(t)
  }, [id])

  useStream((ev) => {
    const tid = ev.type === 'job' || ev.type === 'escalation' ? ev.data.task_id : ev.type === 'task' ? ev.data.id : null
    if (tid && tid !== id && tree.has(tid)) refetchSoon()
    if (tid === id && (ev.type === 'job' || ev.type === 'escalation')) refetchSoon()
    switch (ev.type) {
      case 'task':
        if (ev.data.id === id) setData((cur) => (cur ? { ...cur, task: { ...cur.task, ...ev.data } } : cur))
        else if (ev.data.parent_id === id || data?.subtasks.some((s) => s.id === ev.data.id)) refetchSoon()
        break
      case 'message':
        if (ev.data.task_id === id) setData((cur) => (cur ? { ...cur, messages: upsertById(cur.messages, ev.data) } : cur))
        break
      case 'job':
        if (ev.data.task_id === id) setData((cur) => (cur ? { ...cur, jobs: upsertById(cur.jobs, ev.data, true) } : cur))
        break
      case 'escalation':
        // вопросы подзадач — тоже здесь
        setData((cur) =>
          cur && (ev.data.task_id === id || cur.subtree_ids?.includes(ev.data.task_id ?? -1)) ? { ...cur, escalations: upsertById(cur.escalations, ev.data) } : cur,
        )
        break
      case 'intent':
        if (ev.data.task_id === id) setData((cur) => (cur ? { ...cur, intents: upsertById(cur.intents, ev.data, true) } : cur))
        break
      case 'activity':
        if (ev.data.target_type === 'task' && ev.data.target_id === String(id)) {
          setData((cur) => (cur ? { ...cur, activity: upsertById(cur.activity, ev.data, true) } : cur))
        }
        break
      default:
        break
    }
  })
  useReconnect(detail.reload)

  // открыл готовую задачу — она просмотрена: уходит из «Готово», гаснет зелёный счётчик
  const unseen = data ? isDoneUnseen(data.task) : false
  useEffect(() => {
    if (!unseen) return
    api
      .post(`/api/tasks/${id}/seen`, {})
      .then(() => refreshStats())
      .catch(() => undefined)
  }, [unseen, id])

  const messages = useMemo(() => [...(data?.messages ?? [])].sort((a, b) => a.id - b.id), [data?.messages])
  const msgCount = messages.length

  // переписка: держим прокрутку внизу, если пользователь не листал вверх
  useLayoutEffect(() => {
    if (tab !== 'thread') return
    const el = scroller()
    if (el && stickBottom.current) el.scrollTop = el.scrollHeight
  }, [tab, msgCount])

  useEffect(() => {
    stickBottom.current = true
  }, [tab])

  if (detail.error && !data) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <span className="font-mono text-muted">OMA-{id}</span>
          <IconButton icon="x" label="Закрыть" onClick={closeTask} />
        </div>
        <div className="p-4">
          <ErrorBox error={detail.error} onRetry={detail.reload} />
        </div>
      </div>
    )
  }
  if (!data) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex justify-end px-4 py-3">
          <IconButton icon="x" label="Закрыть" onClick={closeTask} />
        </div>
        <Loading label="Загружаю задачу…" />
      </div>
    )
  }

  const { task } = data
  const openEsc = data.escalations.filter((e) => e.status === 'open')
  const impactMessages = messages.filter((m) => m.kind === 'impact' || m.kind === 'change')
  const activeIntents = data.intents.filter((i) => i.status === 'active').length

  const patch = async (body: Partial<Pick<Task, 'status' | 'title' | 'priority' | 'assignee_agent_id' | 'direction_ids' | 'mode' | 'description'>>) => {
    try {
      const t = await api.patch<Task>(`/api/tasks/${task.id}`, body)
      setData((cur) => (cur ? { ...cur, task: { ...cur.task, ...t } } : cur))
      return true
    } catch (e) {
      toastError(e, 'Не удалось сохранить')
      return false
    }
  }

  return (
    // на телефоне шапка задачи бывает выше экрана — там прокручивается вся шторка целиком, а вкладки прилипают к верху
    // по горизонтали шторка на телефоне не прокручивается: широкое (таблицы, код) листается внутри своего блока
    <div
      ref={outerRef}
      onScroll={onScroll}
      className="flex h-full min-h-0 flex-col max-md:overflow-x-hidden max-md:overflow-y-auto max-md:overscroll-contain max-md:break-words"
    >
      <TaskHeader
        task={task}
        parent={data.parent}
        running={data.jobs.some((j) => j.status === 'running')}
        onPatch={patch}
        onReload={detail.reload}
      />

      {/* вкладки листаются по горизонтали внутри своей полосы (overflow-x-auto в Tabs), а не всю шторку */}
      <div className="flex min-w-0 shrink-0 items-stretch bg-surface max-md:sticky max-md:top-0 max-md:z-10">
        <Tabs<TabId>
          label="Разделы задачи"
          className="min-w-0 flex-1 bg-surface px-3"
          value={tab}
          onChange={pickTab}
          items={[
            { id: 'overview', label: 'Обзор', count: data.subtasks.length || undefined },
            { id: 'thread', label: 'Переписка', count: msgCount },
            { id: 'jobs', label: 'Запуски', count: data.jobs.length },
            { id: 'intents', label: 'Заявки и влияние', count: activeIntents + impactMessages.length },
            { id: 'history', label: 'История', count: data.activity.length },
          ]}
        />
        {headHidden ? (
          <div className="flex shrink-0 items-center border-b border-l border-line px-1.5 md:hidden">
            <IconButton icon="x" label="Закрыть задачу" onClick={closeTask} />
          </div>
        ) : null}
      </div>

      <div
        ref={bodyRef}
        // на телефоне тело не сжимается (прокручивается шторка), но тянется до низа — поле ответа не висит посреди экрана
        className="scroll-thin min-h-0 min-w-0 flex-1 overflow-y-auto max-md:flex-[1_0_auto] max-md:overflow-visible"
        onScroll={onScroll}
      >
        {openEsc.length ? (
          <div className="flex min-w-0 flex-col gap-2.5 border-b border-line bg-warn-soft/40 px-5 py-4 max-sm:px-4">
            <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-warn">
              <Icon name="flag" size={13} />
              Ждёт тебя: {openEsc.length === 1 ? 'вопрос агента' : `${openEsc.length} вопроса агентов`}
            </div>
            {openEsc.map((e) => (
              <EscalationCard
                key={e.id}
                esc={e}
                compact
                showTask={e.task_id !== task.id}
                onAnswered={(u) => setData((cur) => (cur ? { ...cur, escalations: upsertById(cur.escalations, u) } : cur))}
              />
            ))}
          </div>
        ) : null}

        <div className="min-w-0 px-5 py-5 max-sm:px-4 max-sm:py-4">
          <DropOverlay show={dragging} label={tab === 'thread' ? 'Отпустите — файлы прикрепятся к сообщению' : undefined} />
          {tab === 'overview' ? <Overview detail={data} uploading={uploading} onAddFiles={(fs) => void addFiles(fs)} onRemoveFile={(a) => void removeFile(a)} /> : null}
          {tab === 'thread' ? <MessageThread messages={messages} emptyText="Переписки по задаче пока нет" /> : null}
          {tab === 'jobs' ? (
            <JobList
              jobs={[...data.jobs].sort((a, b) => b.id - a.id)}
              initiallyOpen={data.jobs.find((j) => j.status === 'running')?.id ?? null}
            />
          ) : null}
          {tab === 'intents' ? <IntentsTab intents={data.intents} impact={impactMessages} /> : null}
          {tab === 'history' ? <HistoryTab activity={data.activity} /> : null}
        </div>
      </div>

      {tab === 'thread' ? (
        // на телефоне поле ответа прилипает к низу шторки, пока листаешь переписку
        <div className="min-w-0 shrink-0 border-t border-line bg-surface px-5 py-3 max-md:sticky max-md:bottom-0 max-md:z-10 max-sm:px-4">
          <Composer
            taskId={task.id}
            assigneeId={task.assignee_agent_id}
            onSent={(m: Message) => {
              stickBottom.current = true
              setData((cur) => (cur ? { ...cur, messages: upsertById(cur.messages, m) } : cur))
            }}
          />
        </div>
      ) : null}
    </div>
  )
}

function TaskHeader({
  task,
  parent,
  running,
  onPatch,
  onReload,
}: {
  task: Task
  parent: Task | null
  running: boolean
  onPatch: (b: Partial<Task>) => Promise<boolean>
  onReload: () => void
}) {
  const now = useNow()
  const agents = useAgentMap()
  const directions = useLive((s) => s.directions)
  const [editingTitle, setEditingTitle] = useState(false)
  const [title, setTitle] = useState(task.title)
  const [editDirs, setEditDirs] = useState(false)
  const [busy, run] = useBusy()
  const closed = task.status === 'done' || task.status === 'cancelled'
  const cross = task.kind === 'cross' || task.direction_ids.length > 1
  const tz = useLive((s) => s.settings?.agent_timezone)
  const waiting = waitingText(task.waiting_on, (x) => agents.get(x)?.name ?? x, tz)

  useEffect(() => {
    if (!editingTitle) setTitle(task.title)
  }, [task.title, editingTitle])

  const saveTitle = async () => {
    const t = title.trim()
    setEditingTitle(false)
    if (t && t !== task.title) await onPatch({ title: t })
    else setTitle(task.title)
  }

  const cancel = () =>
    run(async () => {
      if (!window.confirm(`Отменить задачу ${task.key}? Агенты прекратят работу над ней.`)) return
      try {
        await api.post<Task>(`/api/tasks/${task.id}/cancel`)
        toast(`${task.key} отменена`, 'success')
        onReload()
      } catch (e) {
        toastError(e, 'Не удалось отменить')
      }
    })

  const retry = () =>
    run(async () => {
      try {
        await api.post(`/api/tasks/${task.id}/retry`)
        toast(`Исполнитель ${task.key} перезапущен`, 'success')
        onReload()
      } catch (e) {
        toastError(e, 'Не удалось перезапустить')
      }
    })

  return (
    <div className="min-w-0 shrink-0 bg-surface px-5 pt-4 pb-3 max-sm:px-4 max-sm:pt-3">
      {/* метки слева переносятся на новую строку, кнопки справа не сжимаются — крестик всегда на экране */}
      <div className="flex items-start gap-2">
        <div className="flex min-h-8 min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-muted">
          <PriorityIcon priority={task.priority} />
          <span className="tnum shrink-0 font-mono whitespace-nowrap text-subtle">{task.key}</span>
          {running ? (
            <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-ok">
              <LiveDot /> работает
            </span>
          ) : null}
          <TaskKindBadge kind={task.kind} cross={cross} />
          {task.risk ? <Badge tone={task.risk === 'high' ? 'danger' : task.risk === 'medium' ? 'warn' : 'neutral'}>{RISK_LABELS[task.risk]}</Badge> : null}
          {task.needs_attention ? <Badge tone="warn">требует внимания</Badge> : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button size="sm" variant="secondary" icon="refresh" onClick={() => void retry()} disabled={busy || task.status === 'cancelled'} title="Перезапустить исполнителя">
            <span className="hidden sm:inline">Перезапустить</span>
          </Button>
          {!closed ? (
            <Button size="sm" variant="danger-quiet" onClick={() => void cancel()} disabled={busy} title="Отменить задачу">
              <span className="sm:hidden">Отменить</span>
              <span className="hidden sm:inline">Отменить задачу</span>
            </Button>
          ) : null}
          <IconButton icon="x" label="Закрыть задачу" onClick={closeTask} />
        </div>
      </div>

      {parent ? (
        <div className="mt-1.5 text-[12px] break-words text-muted">
          подзадача{' '}
          <TaskLink id={parent.id} className="hover:text-fg hover:underline">
            <span className="font-mono">{parent.key}</span> · {parent.title}
          </TaskLink>
        </div>
      ) : null}

      {editingTitle ? (
        <input
          className="ctl mt-2 text-[19px] font-semibold"
          value={title}
          autoFocus
          aria-label="Заголовок задачи"
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => void saveTitle()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void saveTitle()
            } else if (e.key === 'Escape') {
              e.stopPropagation()
              setTitle(task.title)
              setEditingTitle(false)
            }
          }}
        />
      ) : (
        <div className="group mt-2 flex items-start gap-1">
          <h2 className="min-w-0 flex-1 text-[19px] leading-snug font-[680] tracking-[-0.01em] break-words max-sm:text-[17px]">{task.title}</h2>
          {/* на телефоне наведения нет — карандаш виден всегда */}
          <IconButton
            icon="edit"
            size="sm"
            label="Изменить заголовок"
            onClick={() => setEditingTitle(true)}
            className="mt-0.5 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 max-md:opacity-100"
          />
        </div>
      )}

      <div className="mt-3.5 grid grid-cols-2 gap-x-3 gap-y-2.5 sm:grid-cols-4">
        <label className="flex min-w-0 flex-col gap-1">
          <span className="lbl">Статус</span>
          <select
            className="ctl h-8 py-1 text-[13px]"
            value={task.status}
            onChange={(e) => void onPatch({ status: e.target.value as TaskStatus })}
          >
            {TASK_STATUSES.map((s) => (
              <option key={s} value={s}>
                {TASK_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-0 flex-col gap-1">
          <span className="lbl">Приоритет</span>
          <select
            className="ctl h-8 py-1 text-[13px]"
            value={task.priority}
            onChange={(e) => void onPatch({ priority: e.target.value as Priority })}
          >
            {(['low', 'normal', 'high', 'urgent'] as Priority[]).map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABELS[p]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-0 flex-col gap-1">
          <span className="lbl">Исполнитель</span>
          <AgentSelect
            value={task.assignee_agent_id ?? ''}
            onChange={(v) => void onPatch({ assignee_agent_id: v || null })}
            emptyLabel="— не назначен —"
            className="h-8 py-1 text-[13px]"
          />
        </label>
        <label className="flex min-w-0 flex-col gap-1">
          <span className="lbl">Режим</span>
          <select
            className="ctl h-8 py-1 text-[13px]"
            value={task.mode ?? ''}
            onChange={(e) => void onPatch({ mode: (e.target.value || null) as SafetyMode | null })}
          >
            <option value="">как в настройках</option>
            {(Object.keys(SAFETY_MODE_LABELS) as SafetyMode[]).map((m) => (
              <option key={m} value={m}>
                {SAFETY_MODE_LABELS[m]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* длинные названия направлений и сервисов обрезаются по ширине шторки (max-w-full у чипа) */}
      <div className="mt-2.5 flex min-w-0 flex-wrap items-center gap-1.5">
        {editDirs ? (
          directions.map((d) => (
            <DirectionChip
              key={d.id}
              direction={d}
              active={task.direction_ids.includes(d.id)}
              onClick={() =>
                void onPatch({
                  direction_ids: task.direction_ids.includes(d.id)
                    ? task.direction_ids.filter((x) => x !== d.id)
                    : [...task.direction_ids, d.id],
                })
              }
            />
          ))
        ) : task.direction_ids.length ? (
          task.direction_ids.map((d) => <DirectionChip key={d} id={d} />)
        ) : (
          <span className="text-[12px] text-subtle">направления не указаны</span>
        )}
        <button
          type="button"
          className="rounded-ctl px-1.5 text-[12px] font-medium text-accent hover:bg-surface-2"
          onClick={() => setEditDirs((v) => !v)}
        >
          {editDirs ? 'готово' : 'изменить'}
        </button>
        {task.service_ids.length ? <span className="mx-1 h-3 w-px bg-line-strong" aria-hidden="true" /> : null}
        {task.service_ids.map((s) => (
          <ServiceChip key={s} id={s} onClick={() => navigate(`/map?service=${encodeURIComponent(s)}`)} />
        ))}
      </div>

      {waiting ? (
        <div className={cx('mt-2 text-[12.5px] break-words', task.status === 'waiting_human' ? 'font-medium text-warn' : 'text-muted')}>
          {waiting}
        </div>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-subtle">
        <span className="inline-flex max-w-full min-w-0 items-center gap-1">
          <span className="shrink-0">создал</span> <CreatedBy value={task.created_by} />
        </span>
        <span title={formatDateTime(task.created_at)}>создана {timeAgo(task.created_at, now)}</span>
        <span title={formatDateTime(task.updated_at)}>обновлена {timeAgo(task.updated_at, now)}</span>
        {task.closed_at ? <span title={formatDateTime(task.closed_at)}>закрыта {timeAgo(task.closed_at, now)}</span> : null}
        {task.depends_on.length ? (
          <span className="inline-flex max-w-full flex-wrap items-center gap-x-1">
            зависит от:
            {task.depends_on.map((d) => (
              <TaskLink key={d} id={d} className="font-mono hover:text-fg hover:underline">
                OMA-{d}
              </TaskLink>
            ))}
          </span>
        ) : null}
      </div>
    </div>
  )
}

/** Итог первым блоком: коротко и простыми словами; подробный отчёт исполнителя — по раскрытию. */
const NOW_TONE: Record<TaskNow['state'], { icon: 'dot' | 'flag' | 'circle' | 'shield' | 'alertCircle' | 'check' | 'x'; cls: string }> = {
  working: { icon: 'dot', cls: 'text-ok' },
  waiting_owner: { icon: 'flag', cls: 'text-warn' },
  queued: { icon: 'circle', cls: 'text-muted' },
  waiting: { icon: 'circle', cls: 'text-muted' },
  review: { icon: 'shield', cls: 'text-muted' },
  stalled: { icon: 'alertCircle', cls: 'text-danger' },
  done: { icon: 'check', cls: 'text-ok' },
  cancelled: { icon: 'x', cls: 'text-muted' },
}

/** «Сейчас»: что происходит в задаче и её подзадачах, чего ждём, когда следующее действие и обещанный срок. */
function NowCard({ taskId, now }: { taskId: number; now: TaskNow }) {
  const tz = useLive((s) => s.settings?.agent_timezone)
  const clock = useNow()
  if (now.state === 'done' || now.state === 'cancelled') return null
  const seen = new Set<string>()
  const items = now.blockers.filter((b) => {
    const k = `${b.task_id}|${b.kind}|${b.text}`
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
  const tone = NOW_TONE[now.state]
  const etaLate = now.eta ? Date.parse(now.eta) < clock : false
  return (
    <section className="min-w-0 rounded-card border border-line bg-surface-2 px-4 py-3 max-sm:px-3">
      <h3 className={cx('flex items-center gap-1.5 text-[14px] font-semibold', tone.cls)}>
        {now.state === 'working' ? <LiveDot label="идёт работа" /> : <Icon name={tone.icon} size={15} className="shrink-0" />}
        <span className="min-w-0 text-fg [overflow-wrap:anywhere]">Сейчас: {now.headline}</span>
      </h3>
      {items.length ? (
        <ul className="mt-2 flex flex-col gap-1">
          {items.slice(0, 12).map((b, i) => (
            <li key={i} className="flex flex-wrap items-baseline gap-x-1.5 text-[13px] leading-snug text-fg-2">
              {b.task_id !== taskId ? (
                <TaskLink id={b.task_id} className="font-mono text-[12px] text-accent hover:underline" title={b.title}>
                  {b.key}
                </TaskLink>
              ) : null}
              <span className="min-w-0 [overflow-wrap:anywhere]">{b.text}</span>
              {b.until ? <span className="text-muted">— до {agentTime(b.until, tz)}</span> : null}
              {b.since && (b.kind === 'working' || b.kind === 'owner' || b.kind === 'stalled' || b.kind === 'queued') ? (
                <span className="text-subtle">· {b.kind === 'working' ? 'с' : 'с'} {agentTime(b.since, tz)}</span>
              ) : null}
            </li>
          ))}
          {items.length > 12 ? <li className="text-[12px] text-subtle">и ещё {items.length - 12}</li> : null}
        </ul>
      ) : null}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12.5px] text-muted">
        {now.next_at ? <span>Следующее действие: {agentTime(now.next_at, tz)}</span> : null}
        <span className={cx(etaLate && 'font-semibold text-danger')}>
          Срок от исполнителя: {now.eta ? `до ${agentTime(now.eta, tz)}${etaLate ? ' — просрочен' : ''}` : 'не указан'}
        </span>
        <span>Последнее движение: {timeAgo(now.last_activity_at, clock)}</span>
      </div>
    </section>
  )
}

/** Задача Битрикс24, по которой поставлена задача OMA: ссылка в описании или вложение bitrix-task-<номер>.md. */
function linkedBitrix(task: Task, files: string[]): { id: number; url: string } | null {
  const m = /(https?:\/\/[^\s)]+?\/task\/view\/(\d+)\/)/.exec(task.description)
  if (m) return { id: Number(m[2]), url: m[1]! }
  for (const f of files) {
    const a = /^bitrix-task-(\d+)\.md$/.exec(f)
    if (a) return { id: Number(a[1]), url: '' }
  }
  return null
}

/** Сообщение в чат задачи Битрикс24 от лица владельца: черновик агента → правка → отправка одной кнопкой. */
function BitrixChatMessage({ task, b24 }: { task: Task; b24: { id: number; url: string } }) {
  const tz = useLive((s) => s.settings?.agent_timezone)
  const [text, setText] = useState(task.chat_message)
  const [busy, run] = useBusy()
  useEffect(() => setText(task.chat_message), [task.chat_message])
  const send = () =>
    run(async () => {
      try {
        await api.post<Task>(`/api/tasks/${task.id}/bitrix-post`, { message: text.trim() })
        toast(`Отправлено в задачу Битрикс24 #${b24.id} от вашего имени`, 'success')
      } catch (e) {
        toastError(e, 'Не отправлено')
      }
    })
  const where = b24.url ? (
    <a href={b24.url} target="_blank" rel="noreferrer" className="hover:underline">
      #{b24.id}
    </a>
  ) : (
    `#${b24.id}`
  )
  if (!task.chat_message && task.status !== 'done') return null
  return (
    <div className="mt-3 border-t border-line pt-3">
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5 text-[12.5px] font-semibold text-fg-2">
        <Icon name="message" size={14} className="shrink-0 text-subtle" />
        <span className="min-w-0">
          Сообщение в чат задачи Битрикс24 {where} — от вашего имени
        </span>
        {task.chat_posted_at ? (
          <span className="ml-auto text-[12px] font-normal text-ok">отправлено {agentTime(task.chat_posted_at, tz)}</span>
        ) : null}
      </div>
      {!task.chat_message ? (
        <p className="text-[13px] text-muted">Черновика нет — нажмите «Пересобрать итог», агент сведёт итог по всей задаче и подготовит сообщение коллегам.</p>
      ) : task.chat_posted_at ? (
        <p className="text-[14px] leading-relaxed break-words whitespace-pre-wrap text-fg">{task.chat_message}</p>
      ) : (
        <>
          <textarea className="ctl min-h-[110px] text-[14px] leading-relaxed" value={text} onChange={(e) => setText(e.target.value)} />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="primary" icon="send" loading={busy} disabled={!text.trim()} onClick={() => void send()}>
              Отправить в Битрикс24
            </Button>
            <span className="text-[12px] text-subtle">Уйдёт в чат задачи от вашего имени. Текст можно поправить.</span>
          </div>
        </>
      )}
    </div>
  )
}

/** Черновик агента для рабочего чата, не связанного с задачей Битрикс24: OMA туда не пишет — владелец отправляет сам. */
function ChatDraft({ task }: { task: Task }) {
  const [text, setText] = useState(task.chat_message)
  useEffect(() => setText(task.chat_message), [task.chat_message])
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text.trim())
      toast('Текст скопирован', 'success')
    } catch {
      toast('Не удалось скопировать — выделите текст вручную', 'error')
    }
  }
  return (
    <div className="mt-3 border-t border-line pt-3">
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5 text-[12.5px] font-semibold text-fg-2">
        <Icon name="message" size={14} className="shrink-0 text-subtle" />
        <span className="min-w-0">Сообщение для чата — агент подготовил, отправьте от своего имени</span>
      </div>
      <textarea className="ctl min-h-[110px] text-[14px] leading-relaxed" value={text} onChange={(e) => setText(e.target.value)} />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="secondary" icon="copy" disabled={!text.trim()} onClick={() => void copy()}>
          Скопировать
        </Button>
        <span className="text-[12px] text-subtle">OMA этот текст никуда не отправляет. Текст можно поправить перед копированием.</span>
      </div>
    </div>
  )
}

function Outcome({ task, files }: { task: Task; files: string[] }) {
  const [busy, run] = useBusy()
  const b24 = linkedBitrix(task, files)
  if (!task.summary?.trim() && !task.result.trim()) return null
  const { text, brief } = outcomeText(task)
  const resummarize = () =>
    run(async () => {
      if (!window.confirm('Агент сведёт итог по всей задаче (все пункты и что добавлено по ходу)' + (b24 ? ' и подготовит сообщение в чат задачи Битрикс24' : '') + '. В системах он ничего не меняет. Пересобрать?')) return
      try {
        await api.post(`/api/tasks/${task.id}/resummarize`, {})
        toast('Агент пересобирает итог — придёт уведомление, когда будет готово', 'success')
      } catch (e) {
        toastError(e, 'Не удалось')
      }
    })
  const done = task.status === 'done'
  const label =
    task.status === 'done'
      ? 'Итог'
      : task.status === 'review'
        ? 'Итог · на проверке у валидатора'
        : task.status === 'cancelled'
          ? 'Итог · задача отменена'
          : 'Предварительный итог'
  return (
    <section
      className={cx(
        'min-w-0 rounded-card border border-line bg-surface px-4 py-3.5 max-sm:px-3',
        done ? 'shadow-[inset_3px_0_0_var(--orbis-good)]' : 'shadow-[inset_3px_0_0_var(--orbis-warn)]',
      )}
    >
      <h3 className={cx('mb-1.5 flex flex-wrap items-center gap-1.5 text-[12.5px] font-semibold', done ? 'text-ok' : 'text-warn')}>
        <Icon name={done ? 'check' : task.status === 'review' ? 'shield' : 'circle'} size={14} className="shrink-0" /> {label}
        {done ? (
          <Button size="xs" variant="ghost" icon="refresh" className="ml-auto font-normal" loading={busy} onClick={() => void resummarize()}>
            Пересобрать итог
          </Button>
        ) : null}
      </h3>
      {text ? <p className="text-[15px] leading-relaxed break-words text-fg">{text}</p> : null}
      {text && !brief ? <p className="mt-1 text-[12px] text-subtle">Краткого итога агент не оставил — показано начало подробного отчёта.</p> : null}
      {task.result.trim() ? (
        <Collapsible summary="Подробный отчёт исполнителя" defaultOpen={!text} className={cx(text && 'mt-3 border-t border-line pt-2.5')}>
          <Markdown text={task.result} />
        </Collapsible>
      ) : null}
      {b24 ? <BitrixChatMessage task={task} b24={b24} /> : task.chat_message.trim() ? <ChatDraft task={task} /> : null}
    </section>
  )
}

function Overview({
  detail,
  uploading,
  onAddFiles,
  onRemoveFile,
}: {
  detail: TaskDetail
  uploading: { name: string; p: number; left: number } | null
  onAddFiles: (files: File[]) => void
  onRemoveFile: (a: AttachmentInfo) => void
}) {
  const { task } = detail
  const files = detail.attachments ?? []
  const answered = detail.escalations.filter((e) => e.status !== 'open')
  return (
    <div className="flex flex-col gap-5">
      {detail.now ? <NowCard taskId={task.id} now={detail.now} /> : null}
      <Outcome task={task} files={files.map((f) => f.name)} />

      <section>
        <h3 className="ct mb-2">Описание</h3>
        {task.description.trim() ? <Markdown text={task.description} /> : <p className="text-muted">Описания нет.</p>}
      </section>

      <section>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h3 className="ct">Вложения{files.length ? ` · ${files.length}` : ''}</h3>
          <span className="ml-auto" />
          {uploading ? (
            <span className="tnum max-w-full min-w-0 truncate text-[12px] text-muted">
              Загружаю «{uploading.name}» — {Math.round(uploading.p * 100)}%{uploading.left > 1 ? `, ещё ${uploading.left - 1}` : ''}
            </span>
          ) : (
            <span className="hidden text-[12px] text-subtle sm:inline">перетащите файлы в окно или вставьте скриншот (⌘V)</span>
          )}
          <FilePicker onFiles={onAddFiles} size="xs" label="Прикрепить" />
        </div>
        {files.length ? <AttachmentGrid items={files} taskId={task.id} onDelete={onRemoveFile} /> : <p className="text-[13px] text-muted">Файлов нет.</p>}
      </section>

      {task.acceptance.trim() ? (
        <section>
          <h3 className="ct mb-2">Критерии приёмки</h3>
          <div className="min-w-0 rounded-card border border-line bg-surface px-4 py-3 shadow-card max-sm:px-3">
            <Markdown text={task.acceptance} />
          </div>
        </section>
      ) : null}

      <section>
        <h3 className="ct mb-2 flex items-center gap-2">
          Подзадачи
          {detail.subtasks.length ? (
            <span className="font-normal normal-case">
              <SubtaskProgress
                done={detail.subtasks.filter((s) => s.status === 'done').length}
                total={detail.subtasks.length}
              />
            </span>
          ) : null}
        </h3>
        {detail.subtasks.length ? (
          <SubtaskTree items={detail.subtasks} rootId={task.id} />
        ) : (
          <p className="text-muted">
            {task.status === 'inbox' || task.status === 'triage'
              ? 'Штаб ещё не разложил задачу на подзадачи.'
              : 'Подзадач нет.'}
          </p>
        )}
      </section>

      {answered.length ? (
        <section>
          <h3 className="ct mb-2">Вопросы к тебе — отвеченные</h3>
          <div className="flex flex-col gap-2">
            {answered.map((e) => (
              <EscalationCard key={e.id} esc={e} compact showTask={false} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  )
}

function SubtaskTree({ items, rootId }: { items: TaskCard[]; rootId: number }) {
  const byParent = useMemo(() => {
    const m = new Map<number, TaskCard[]>()
    const ids = new Set(items.map((i) => i.id))
    for (const it of items) {
      const p = it.parent_id !== null && ids.has(it.parent_id) ? it.parent_id : rootId
      const l = m.get(p) ?? []
      l.push(it)
      m.set(p, l)
    }
    for (const l of m.values()) l.sort((a, b) => a.id - b.id)
    return m
  }, [items, rootId])

  const render = (pid: number, depth: number): ReactNode => {
    const list = byParent.get(pid)
    if (!list?.length) return null
    return (
      <ul className={cx('flex min-w-0 flex-col', depth > 0 && 'ml-5 border-l border-line pl-2 max-sm:ml-2.5 max-sm:pl-1.5')}>
        {list.map((s) => (
          <li key={s.id}>
            {/* на телефоне: метки и исполнитель первой строкой, заголовок целиком второй */}
            <TaskLink
              id={s.id}
              className="flex min-w-0 flex-wrap items-center gap-2 rounded-ctl px-2 py-1.5 hover:bg-surface-2 max-sm:gap-y-1 sm:flex-nowrap"
            >
              <StatusBadge status={s.status} />
              <span className="tnum shrink-0 font-mono text-[11.5px] text-subtle">{s.key}</span>
              <span
                className={cx(
                  'min-w-0 flex-1 truncate max-sm:order-last max-sm:basis-full max-sm:whitespace-normal',
                  s.status === 'done' && 'text-muted',
                )}
              >
                {s.title}
              </span>
              {s.running ? <LiveDot label="работает" /> : null}
              {s.open_escalations ? (
                <Badge tone="danger">
                  <Icon name="flag" size={11} />
                  {s.open_escalations}
                </Badge>
              ) : null}
              <SubtaskProgress done={s.subtasks_done} total={s.subtasks_total} />
              <span className="w-[150px] shrink-0 text-[12px] max-sm:w-auto max-sm:min-w-0 max-sm:flex-1 max-sm:text-right">
                {s.assignee_agent_id ? <AgentLabel id={s.assignee_agent_id} size="xs" className="max-w-full" /> : <span className="text-subtle">—</span>}
              </span>
            </TaskLink>
            {render(s.id, depth + 1)}
          </li>
        ))}
      </ul>
    )
  }
  return <div className="rounded-card border border-line bg-surface p-1 shadow-card">{render(rootId, 0)}</div>
}

export function IntentRow({ it, showTask }: { it: Intent; showTask?: boolean }) {
  const now = useNow()
  const active = it.status === 'active'
  return (
    <li className={cx('min-w-0 rounded-card border bg-surface px-3 py-2', active ? 'border-line' : 'border-line opacity-70')}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
        <AgentLabel id={it.agent_id} size="xs" link className="max-w-full font-medium" />
        <Icon name="arrowRight" size={12} className="shrink-0 text-subtle" />
        <Link to={`/map?service=${encodeURIComponent(it.service_id)}`} className="max-w-full min-w-0 hover:underline">
          <ServiceChip id={it.service_id} />
        </Link>
        {it.area ? <span className="min-w-0 font-mono text-[11.5px] text-muted [overflow-wrap:anywhere]">{it.area}</span> : null}
        <Badge tone={INTENT_KIND_TONES[it.kind] ?? 'neutral'}>{INTENT_KIND_LABELS[it.kind] ?? it.kind}</Badge>
        {showTask && it.task_id ? (
          <TaskLink id={it.task_id} className="font-mono text-[11.5px] text-muted hover:underline">
            OMA-{it.task_id}
          </TaskLink>
        ) : null}
        <span className="ml-auto shrink-0 text-[11.5px] whitespace-nowrap text-subtle" title={formatDateTime(it.expires_at)}>
          {active ? `до ${timeUntil(it.expires_at, now).replace(/^через /, '')}` : it.status === 'released' ? 'снята' : 'истекла'}
        </span>
      </div>
      {it.description ? <div className="mt-1 text-[12.5px] break-words">{it.description}</div> : null}
      {it.outcome ? <div className="mt-1 text-[12px] break-words text-muted">итог: {it.outcome}</div> : null}
    </li>
  )
}

function IntentsTab({ intents, impact }: { intents: Intent[]; impact: Message[] }) {
  const sorted = [...intents].sort((a, b) => Number(b.status === 'active') - Number(a.status === 'active') || b.id - a.id)
  return (
    <div className="flex flex-col gap-5">
      <section>
        <h3 className="ct mb-1.5">Заявки на изменения</h3>
        <p className="mb-2 text-[12px] text-muted">
          Агент объявляет, что собирается менять, чтобы другие не трогали то же место одновременно.
        </p>
        {sorted.length ? (
          <ul className="flex flex-col gap-1.5">
            {sorted.map((it) => (
              <IntentRow key={it.id} it={it} />
            ))}
          </ul>
        ) : (
          <Empty icon="shield">Заявок по задаче нет</Empty>
        )}
      </section>
      <section>
        <h3 className="ct mb-2">Анализ влияния</h3>
        {impact.length ? (
          <div className="flex flex-col gap-2">
            {impact.map((m) => (
              <MessageItem key={m.id} m={m} />
            ))}
          </div>
        ) : (
          <Empty icon="graph">Сообщений о влиянии на другие сервисы пока нет</Empty>
        )}
      </section>
    </div>
  )
}

export function ActivityRow({ a, showTarget }: { a: Activity; showTarget?: boolean }) {
  const now = useNow()
  const taskId = a.target_type === 'task' && a.target_id ? Number(a.target_id) : null
  return (
    <li className="flex items-start gap-2.5 py-1.5 text-[12.5px]">
      {/* длинное имя автора обрезается, чтобы текст события не сжимался в столбик */}
      <span className="mt-px flex min-w-0 shrink-0 max-md:max-w-[40%]">
        <ActorLabel type={a.actor_type} id={a.actor_id} size="xs" />
      </span>
      <span className="min-w-0 flex-1 text-muted [overflow-wrap:anywhere]">
        <span className="text-fg">{a.summary || a.action}</span>
        {showTarget && taskId ? (
          <>
            {' '}
            <TaskLink id={taskId} className="font-mono text-[11.5px] hover:underline">
              OMA-{taskId}
            </TaskLink>
          </>
        ) : null}
      </span>
      <time className="tnum shrink-0 text-[11px] text-subtle" title={formatDateTime(a.created_at)}>
        {timeAgo(a.created_at, now)}
      </time>
    </li>
  )
}

function HistoryTab({ activity }: { activity: Activity[] }) {
  const sorted = [...activity].sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id - a.id)
  if (!sorted.length) return <Empty icon="list">Истории пока нет</Empty>
  return (
    <ul className="divide-y divide-line rounded-card border border-line bg-surface px-3.5 shadow-card">
      {sorted.map((a) => (
        <ActivityRow key={a.id} a={a} />
      ))}
    </ul>
  )
}
